import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { providerApi } from '../../services/api';
import { storeActionError } from '../../utils/storeFulfilment';

const MAX_LEN = 100;

/**
 * "Mark shipped": carrier and tracking number are both optional (the server accepts a bare ship),
 * each at most 100 characters. On success `onDone(updatedOrder)` runs; a failure stays in the
 * dialog so the store can fix it or retry.
 */
export function ShipOrderDialog({ open, order, onClose, onDone }) {
  const { t } = useTranslation();
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setCarrier('');
      setTrackingNumber('');
      setError('');
    }
  }, [open, order?.id]);

  const tooLong = carrier.length > MAX_LEN || trackingNumber.length > MAX_LEN;

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const updated = await providerApi.shipStoreOrder(order.id, { carrier, trackingNumber });
      onDone(updated);
    } catch (err) {
      setError(storeActionError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('fulfilment.shipTitle', { id: order?.id })}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('fulfilment.shipHelp')}</DialogContentText>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth
          margin="dense"
          label={t('fulfilment.carrier')}
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          error={carrier.length > MAX_LEN}
          helperText={carrier.length > MAX_LEN ? t('fulfilment.tooLong', { max: MAX_LEN }) : ' '}
          disabled={busy}
        />
        <TextField
          fullWidth
          margin="dense"
          label={t('fulfilment.trackingNumber')}
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          error={trackingNumber.length > MAX_LEN}
          helperText={trackingNumber.length > MAX_LEN ? t('fulfilment.tooLong', { max: MAX_LEN }) : ' '}
          inputProps={{ dir: 'ltr' }}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || tooLong}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          {t('fulfilment.confirmShipped')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Mark delivered" is irreversible and starts the buyer's return window, so it is confirmed. */
export function DeliverOrderDialog({ open, order, onClose, onDone }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) setError('');
  }, [open, order?.id]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const updated = await providerApi.deliverStoreOrder(order.id);
      onDone(updated);
    } catch (err) {
      setError(storeActionError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('fulfilment.deliverTitle', { id: order?.id })}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <DialogContentText>{t('fulfilment.deliverHelp')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="success"
          onClick={submit}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          {t('fulfilment.confirmDelivered')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
